/**
 * useInvalidateTransactionsOnSyncCompleted
 *
 * Subscribes to the SSE `sync_completed` stream and invalidates the React
 * Query caches whose underlying data the import pipeline mutates:
 *   - transactions (the import promotes rows into bat.transactions)
 *   - balances (derived from transactions)
 *   - imports list (status flips to "completed")
 *   - needs-attention tray (the import stages rows needing manual review)
 *
 * Issue #346 — without this, the /transactions page keeps showing stale
 * cache for up to 5 minutes (default staleTime) after an import lands,
 * forcing the user to F5 to see imported rows. With this hook mounted at
 * AuthenticatedLayout level, every sync_completed event invalidates the
 * relevant queries; whatever page the user lands on next refetches.
 *
 * Behavior:
 *   - Mounts once (intended to live in `AuthenticatedLayout`).
 *   - Dedupes per `jobId` so a replayed `sync_completed` event (reconnect,
 *     StrictMode double-mount) does NOT trigger duplicate invalidations.
 *   - Tenant scoping relies on the backend: the SSE stream is authenticated
 *     and the server only emits events for the requesting tenant. There is
 *     NO client-side cross-tenant filter here — if leakage ever becomes a
 *     documented risk, surface the current tenant via context and check it.
 */

import { useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { balanceKeys, importKeys, transactionKeys } from '@/platform/query-client'
import type { SseSyncEvent } from '@/platform/sync/types'
import { syncClient } from '@/platform/websocket/syncClient'

import { needsAttentionKeys } from './useNeedsAttention'
import { createSeenJobIdTracker } from '../utils/seen-job-id-tracker'

// Module-level dedup tracker — survives StrictMode double-mounts and
// re-renders. Bounded FIFO, see createSeenJobIdTracker.
const seenJobIdTracker = createSeenJobIdTracker()

// Reset hook used by tests to clear the module-level dedup set between cases.
// Exported only for tests; production callers must not import this.
export function __resetSeenJobIdsForTests(): void {
  seenJobIdTracker.reset()
}

export const useInvalidateTransactionsOnSyncCompleted = (): void => {
  const queryClient = useQueryClient()

  useEffect(() => {
    const handleSyncCompleted = (event: SseSyncEvent): void => {
      if (!event.jobId || seenJobIdTracker.hasSeen(event.jobId)) return
      seenJobIdTracker.markSeen(event.jobId)

      void queryClient.invalidateQueries({ queryKey: transactionKeys.all })
      void queryClient.invalidateQueries({ queryKey: balanceKeys.all })
      void queryClient.invalidateQueries({ queryKey: importKeys.all })
      // The import pipeline also stages needs-attention rows; without this the
      // tray (and its badge count) stays stale until its 30s staleTime lapses.
      void queryClient.invalidateQueries({ queryKey: needsAttentionKeys.all })
    }

    const unsubscribe = syncClient.on('sync_completed', handleSyncCompleted)
    // Acquire a refcount on the shared SSE connection.
    syncClient.connect()

    return (): void => {
      unsubscribe()
      syncClient.disconnect()
    }
    // queryClient is stable from QueryClientProvider — re-running this effect
    // would tear down + reopen the SSE connection unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
